import { Controller } from '@hotwired/stimulus'
import { marked } from 'marked'

import consumer from '../../channels/consumer'
import { sanitizeHtml } from '../../utils/sanitize'
import { getRoomMessages } from '../../services/messagesService'
import { divider, serializeMessage } from './chatTemplates'
import i18next from 'i18next'
import ChatContext from './chatContext'


// Connects to data-controller="chats--messages"
export default class extends Controller {
  static targets = ['messageLister', 'newMessage', 'debugContext', 'contextField']
  static values = {
    projectId: String,
    roomId: String,
    siteId: String,
    environment: String,
    currentUserId: String,
    organizationId: String,
  }

  connect() {
    this.currentDate = new Date()
    this.prevDate = null
    this.streamingMessages = new Map() // Track streaming messages eg. { 'abc123': { accumulated: '...', isStreaming: true } }

    this._updateContextField()
    ChatContext.observeDialogContainers()

    if (this.roomIdValue) {
      this._updateRoomValue()
      this.connectToRoom()
    }

    this.messageListerTarget.addEventListener('newMessage', this._onNewMessage)
    this.messageListerTarget.addEventListener('selectRoom', this._onSelectRoom)

    // Clear input immediately on submit (don't wait for server response)
    this.formElement = this.element.querySelector('form')
    if (this.formElement) {
      this.formElement.addEventListener('turbo:submit-start', this._onFormSubmitStart)
    }

    // Subscribe to context changes
    this.contextUnsubscribe = ChatContext.subscribe(() => {
      this._updateContextField()
      this._updateDebugContext()
    })

    this._updateDebugContext()
  }

  disconnect() {
    this.unsubscribeFromRoom();

    if (this.messageListerTarget) {
      this.messageListerTarget.removeEventListener('newMessage', this._onNewMessage)
      this.messageListerTarget.removeEventListener('selectRoom', this._onSelectRoom)
    }

    this.contextUnsubscribe?.()

    if (this.formElement) {
      this.formElement.removeEventListener('turbo:submit-start', this._onFormSubmitStart)
    }

    this.streamingMessages.clear()
  }

  unsubscribeFromRoom() {
    if (this.messagesChannel) {
      try {
        this.messagesChannel.unsubscribe()
      } catch (e) {
        console.warn('Error unsubscribing from messagesChannel:', e)
      }
      this.messagesChannel = null
    }
  }

  newConversation() {
    this.selectRoomById(undefined)
    this.newMessageTarget.value = ''
  }

  selectRoom(event) {
    event.preventDefault()
    this.selectRoomById(event.params.roomId.length ? event.params.roomId : undefined)
    this.newMessageTarget.focus()
  }

  selectRoomById(roomId) {
    this.unsubscribeFromRoom()
    this.roomIdValue = roomId
    this._updateRoomValue()
    this.messageListerTarget.innerHTML = ''

    if (this.roomIdValue) {
      this.connectToRoom()
    }

    this._updateDebugContext()
  }

  connectToRoom() {
    this._updateContextField()
    this._updateRoomValue()
    this.messagesChannel = consumer.subscriptions.create(
      {
        channel: 'MessagesChannel',
        room: this.roomIdValue,
      },
      {
        connected: async () => {
          const { data: messages } = await getRoomMessages(this.roomIdValue)
          this.messageListerTarget.innerHTML += this.addDividers(this.messageListerTarget.innerHTML, messages)
          this.scrollToBottom()
        },

        disconnected (e) {
          console.log('Disconnected', e)
        },

        received: (data) => {
          const { type, content, message } = data
          switch (type) {
            case 'stream_start':
              this.handleStreamStart(message)
              break
            case 'stream_chunk':
              this.handleStreamChunk(message, content)
              break
            case 'message_complete':
              this.handleStreamComplete(message)
              break
            default:
              console.error('Unknown message type', type, message)
              break
          }
        },
      },
    )
  }

  addMessage(message) {
    const messageDate = new Date(message.created_at).toDateString()
    let html = ''
    if (messageDate !== this.prevDate) {
      html += `${divider(messageDate)}`
      this.prevDate = messageDate
    }

    const messageElement = document.getElementById(`msg-content-${message.id}`)
    if (!messageElement) {
      this.messageListerTarget.innerHTML += serializeMessage(this.messageListerTarget.innerHTML, message)
    } else {
      this.setMessageContent(message.id, message.body)
    }
  }

  setMessageContent(messageId, content) {
    const contentElement = document.getElementById(`msg-content-${messageId}`)
    try {
      // Raw HTML (e.g. typing indicator) should not be parsed as markdown
      contentElement.innerHTML = content.includes('typing-indicator') ? content : sanitizeHtml(marked.parse(content))
    } catch (error) {
      contentElement.innerHTML = sanitizeHtml(content)
    }
  }

  handleStreamStart(message) {
    this._removeTemporaryTypingIndicator()
    this.addMessage(message)
    this.streamingMessages.set(message.id, { accumulated: '', isStreaming: true })
    this.scrollToBottom()
  }

  handleStreamChunk(message, content) {
    console.log('[handleStreamChunk] message.id:', message.id, 'streamingMessages.has:', this.streamingMessages.has(message.id))
    let streamState = this.streamingMessages.get(message.id)
    if (!streamState || !streamState.isStreaming) {
      // Fallback: If we missed stream_start, initialize streaming state
      console.warn('[handleStreamChunk] Fallback: initializing streaming state for message:', message.id)
      this._removeTemporaryTypingIndicator()
      this.addMessage(message)
      this.streamingMessages.set(message.id, { accumulated: '', isStreaming: true })
      streamState = this.streamingMessages.get(message.id)
    }
    streamState.accumulated += content
    const messageElementRaw = document.getElementById(`msg-content-raw-${message.id}`)
    if (messageElementRaw) {
      messageElementRaw.innerHTML = sanitizeHtml(streamState.accumulated)
    }
    this.setMessageContent(message.id, streamState.accumulated)
    this.scrollToBottom()
  }

  handleStreamComplete(message) {
    this._removeTemporaryTypingIndicator()
    const streamState = this.streamingMessages.get(message.id)
    if (streamState) {
      streamState.isStreaming = false
    }
    this.addMessage(message)
    this.streamingMessages.delete(message.id)
    this.scrollToBottom()
  }

  addDividers(existingHtml, messages) {
    return messages.reduce((html, message) => {
      const messageDate = new Date(message.created_at).toDateString()
      if (messageDate !== this.prevDate) {
        html += `${divider(messageDate)}${serializeMessage(existingHtml, message)}`
        this.prevDate = messageDate
      } else {
        html += serializeMessage(existingHtml, message)
      }
      return html
    }, '')
  }

  scrollToBottom() {
    this.messageListerTarget.scroll({ top: this.messageListerTarget.scrollHeight + 500, behavior: 'smooth' })
  }

  discardMessageClick(event) {
    const messageId = event.target.dataset.messageId

    if (!confirm(i18next.t('discard_confirm', { ns: 'controllers__chats__messages_controller' }))) {
      return
    }

    const messageElement = document.getElementById(`msg-${messageId}`)
    messageElement.style.opacity = '0.3'
    document.getElementById(`discard-message-${messageId}`).disabled = true

    fetch(`/messages/${messageId}/discard`, {
      method: 'POST',
      headers: {
        Accept: 'text/vnd.turbo-stream.html',
        'X-CSRF-Token': document.querySelector('meta[name=csrf-token]').content,
      },
      body: { discard: true },
    })
      .then(response => response.text())
      .then(html => {
        Turbo.renderStreamMessage(html)
        messageElement?.remove()
      })

  }

  _updateRoomValue() {
    this.element.querySelector('[data-room-id]').value = this.roomIdValue
  }

  _onNewMessage = (event) => {
    const { message } = event.detail
    if (!this.roomIdValue) {
      this.roomIdValue = message.room_id
      this.connectToRoom()
      this._updateDebugContext()
    }
    this.addMessage(message)
    this.scrollToBottom()
  }

  _onFormSubmitStart = () => {
    this.newMessageTarget.value = ''
    this.newMessageTarget.focus()
    this._showTemporaryTypingIndicator()
  }

  _showTemporaryTypingIndicator() {
    // Remove any existing temporary indicator
    this._removeTemporaryTypingIndicator()

    const indicator = document.createElement('div')
    indicator.id = 'temporary-typing-indicator'
    indicator.className = 'd-flex flex-row justify-content-start'
    indicator.innerHTML = `
      <div>
        <div class="small p-2 ms-3 mb-1 rounded-3 msg-other">
          <div class="typing-indicator"><span class="fa fa-lightbulb"></span> &nbsp;<span class="dots">...</span></div>
        </div>
      </div>`
    this.messageListerTarget.appendChild(indicator)
    this.scrollToBottom()
  }

  _removeTemporaryTypingIndicator() {
    document.getElementById('temporary-typing-indicator')?.remove()
  }

  _onSelectRoom = (event) => {
    const { message } = event.detail
    selectRoomById(message.room_id)
  }

  _updateContextField() {
    if (!this.hasContextFieldTarget) return
    this.contextFieldTarget.value = JSON.stringify(ChatContext.getContext())
  }

  _updateDebugContext() {
    if (this.environmentValue !== 'development' || !this.hasDebugContextTarget) return

    const debugData = {
      user_id: this.currentUserIdValue || null,
      organization_id: this.organizationIdValue || null,
      room_id: this.roomIdValue || null,
      context: ChatContext.getContext(),
    }

    this.debugContextTarget.textContent = JSON.stringify(debugData, null, 2)
  }

  copyDebugContext(event) {
    if (!this.hasDebugContextTarget) return

    const text = this.debugContextTarget.textContent
    navigator.clipboard.writeText(text).then(() => {
      const button = event.currentTarget
      const icon = button.querySelector('i')
      icon.classList.replace('bi-clipboard', 'bi-clipboard-check')
      setTimeout(() => icon.classList.replace('bi-clipboard-check', 'bi-clipboard'), 1500)
    })
  }
}
